﻿import { useState } from 'react';
import SidebarHeader from './SidebarHeader';
import SearchInput from './SearchInput';
import SellerModeToggle from './SellerModeToggle';
import FilterTabs from './FilterTabs';
import ConversationList from './ConversationList';
import ArchivedChats from './ArchivedChats';
import { INBOX_LABELS, TAB_DEFINITIONS } from '../../lib/chatLabels';

/**
 * Left column - fixed 320px.
 *
 * Everything about conversations comes from `useInbox`, which reads the real
 * API. The only local state left here is nothing: the bell now reflects real
 * unread messages and switches the list to the Unread tab.
 */
export default function Sidebar({ inbox, user, socketStatus, isDark, onToggleTheme }) {
  const [showUnreadOnly, setShowUnreadOnly] = useState(false);

  const inboxLabel = INBOX_LABELS[inbox.side] ?? INBOX_LABELS.buyer;

  const handleBell = () => {
    const next = !showUnreadOnly;
    setShowUnreadOnly(next);
    inbox.setActiveTab(next ? 'unread' : 'all');
  };

  return (
    // Full width as the phone's list pane; a fixed column from md up (300px on
    // tablets so the chat beside it keeps a comfortable measure, 320px on
    // desktop exactly as before).
    <aside className="relative flex h-full w-full shrink-0 flex-col border-r border-line bg-surface-2 pt-[env(safe-area-inset-top)] md:w-[300px] lg:w-[320px]">
      <SidebarHeader
        user={user}
        unreadCount={inbox.totalUnreadMessages}
        notificationsOpen={showUnreadOnly}
        onOpenNotifications={handleBell}
        socketStatus={socketStatus}
        isDark={isDark}
        onToggleTheme={onToggleTheme}
      />

      <div className="space-y-3 px-4 pb-3">
        <SearchInput value={inbox.searchQuery} onChange={inbox.setSearchQuery} />
        <SellerModeToggle
          enabled={inbox.side === 'seller'}
          inboxLabel={inboxLabel}
          disabled
          hint={`Your account role decides which inbox the server returns`}
        />
      </div>

      <FilterTabs
        tabs={TAB_DEFINITIONS}
        activeTab={inbox.activeTab}
        counts={inbox.counts}
        onChange={inbox.setActiveTab}
      />

      {inbox.status === 'error' ? (
        <div className="flex flex-1 items-center justify-center px-6 py-10 text-center">
          <div>
            <p className="text-[13px] font-medium text-ink">Couldnâ€™t load your inbox</p>
            <p className="mt-1 text-[11.5px] text-ink-3">{inbox.error?.message}</p>
            <button
              type="button"
              onClick={inbox.refresh}
              className="mt-3 rounded-lg border border-line px-3 py-1.5 text-[12px] font-medium text-ink-2 transition hover:border-brand-300 hover:bg-brand-soft hover:text-accent"
            >
              Try again
            </button>
          </div>
        </div>
      ) : (
        <ConversationList
          conversations={inbox.visibleConversations}
          selectedId={inbox.selectedId}
          onSelect={inbox.selectConversation}
          isFiltered={inbox.isFiltered}
          onResetFilters={inbox.resetFilters}
          loading={inbox.status === 'loading'}
        />
      )}

      {/* Delete only hides a chat for this account; this is how it comes back. */}
      <ArchivedChats
        chats={inbox.archivedChats}
        onRestore={inbox.restoreChat}
        onRestoreAll={inbox.restoreAllChats}
      />
    </aside>
  );
}
